import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { useToast } from "../lib/toast";
import { Card } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { Drawer } from "../components/ui/Drawer";

interface Ncr {
  id: number;
  ncrNumber: string;
  materialNumber: string;
  description: string;
  status: "open" | "disputed" | "resolved";
  openedAt: number;
}

/** eQuality — non-conformance inbox, adapter-backed (sandbox data mirrors
 * ETQ; Wave 2 live mode calls the real ETQ REST API — see 04 §Integration
 * Architecture). */
export function Quality() {
  const [respondTo, setRespondTo] = useState<Ncr | null>(null);
  const [dispositionType, setDispositionType] = useState("");
  const [responseText, setResponseText] = useState("");
  const toast = useToast();
  const qc = useQueryClient();

  const { data } = useQuery({ queryKey: ["ncrs"], queryFn: () => api.get<{ rows: Ncr[] }>("/quality/non-conformances").then((r) => r.rows) });

  const respondMutation = useMutation({
    mutationFn: () => api.post("/quality/respond", { ncrId: respondTo!.id, dispositionType, responseText }),
    onSuccess: () => {
      toast("Corrective action submitted");
      setRespondTo(null);
      qc.invalidateQueries({ queryKey: ["ncrs"] });
    },
    onError: () => toast("Submission failed", "error"),
  });

  return (
    <Card>
      <div className="p-5 font-semibold text-sm border-b border-slate-100 dark:border-white/10">Non-Conformance Inbox</div>
      <div className="divide-y divide-slate-100 dark:divide-white/5">
        {(data ?? []).map((ncr) => (
          <div key={ncr.id} className="flex items-center gap-3 px-5 py-3.5">
            <span className={`h-2 w-2 rounded-full shrink-0 ${ncr.status === "open" ? "bg-red-500" : ncr.status === "disputed" ? "bg-amber-500" : "bg-emerald-500"}`} />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate">
                {ncr.ncrNumber} — {ncr.description}
              </div>
              <div className="text-xs text-slate-400 truncate">Material {ncr.materialNumber}</div>
            </div>
            {ncr.status === "open" && (
              <Button variant="outline" onClick={() => setRespondTo(ncr)}>
                Respond
              </Button>
            )}
          </div>
        ))}
        {!data?.length && <div className="px-5 py-8 text-center text-slate-400 text-sm">No open non-conformances.</div>}
      </div>

      <Drawer open={!!respondTo} onClose={() => setRespondTo(null)} title={respondTo ? `Respond — ${respondTo.ncrNumber}` : ""}>
        <div className="space-y-3">
          <div>
            <label className="text-[11px] font-semibold text-slate-500">Disposition Type</label>
            <input className="mt-1 w-full text-sm border border-slate-200 dark:border-white/10 rounded-lg px-3 py-2 bg-white dark:bg-white/5" value={dispositionType} onChange={(e) => setDispositionType(e.target.value)} />
          </div>
          <div>
            <label className="text-[11px] font-semibold text-slate-500">Corrective Action</label>
            <textarea className="mt-1 w-full text-sm border border-slate-200 dark:border-white/10 rounded-lg px-3 py-2 bg-white dark:bg-white/5" rows={4} value={responseText} onChange={(e) => setResponseText(e.target.value)} />
          </div>
          <Button className="w-full justify-center" onClick={() => respondMutation.mutate()} disabled={respondMutation.isPending}>
            Submit Response
          </Button>
        </div>
      </Drawer>
    </Card>
  );
}
